import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useTracker } from '@archipro-website/tracker';

/**
 * Marker attribute carried by the element that starts a page of grid results
 * (1-based page number). {@link useViewportPageSync} resolves the reader's
 * current page from these markers.
 */
export const VIEWPORT_PAGE_MARKER_ATTR = 'data-directory-page';

/** Spreadable marker props for the element that starts the given page. */
export const viewportPageMarkerProps = (page: number) => ({
    [VIEWPORT_PAGE_MARKER_ATTR]: page,
});

/**
 * Measurements run at most once per interval while scrolling (with a trailing
 * call at settle), so the marker query + rect reads never run per frame and
 * `replaceState` stays far under Safari's ~100-per-30s budget even when
 * scrubbing through a deep session.
 */
const MEASURE_INTERVAL_MS = 250;

/** Keys that scroll the page — the only keydowns that count as scroll intent. */
const SCROLL_INTENT_KEYS = new Set([
    ' ',
    'PageDown',
    'PageUp',
    'ArrowDown',
    'ArrowUp',
    'Home',
    'End',
]);

interface UseViewportPageSyncArgs {
    /** Sync only when the surface actually pages; off for single-page sets. */
    enabled: boolean;
    /**
     * When set, page changes also log the DirectoryPageChange tracking event
     * with this as the category (the directory browse pages). Omit on
     * surfaces that only want the URL kept accurate (profile tabs).
     */
    trackCategory?: string;
}

/**
 * Keeps `?page=N` pointing at the page of results the reader is actually
 * looking at.
 *
 * The paging hooks used to rewrite the param when a fetch started (or
 * finished), but the infinite-scroll sentinel prefetches ~2000px ahead — the
 * URL would run one or two pages past the viewport, and a fast scroll could
 * skip page numbers entirely (QA: "still jumping pages as you scroll").
 * Instead, the element starting each page carries
 * {@link VIEWPORT_PAGE_MARKER_ATTR}, and each throttled measurement picks
 * the deepest marker above the viewport midline: that page is what the
 * reader sees, scrolling in either direction. Page 1 clears the param.
 *
 * Guards, each load-bearing:
 * - Sync arms on the first user gesture (wheel / touch / scroll-intent key /
 *   pointer): the deep-link `?page=N` landing scroll and late layout shifts
 *   fire programmatic scroll events before the reader has touched anything,
 *   and honouring those would overwrite the deep-link param with wherever
 *   the landing settled. Surfaces whose infinite scroll is gated behind a
 *   View More click always arm on that click's pointerdown.
 * - Writes bail when `window.location` no longer matches the route this
 *   hook rendered under (a POP swaps the URL while the outgoing grid is
 *   still mounted; an armed momentum scroll must not stamp the old page
 *   onto the incoming route's URL).
 * - Sync disarms whenever the route identity changes, so each result set
 *   must be armed by its own gesture. On an in-grid PUSH (category, sort,
 *   filters) the live URL and the router location agree the moment the
 *   navigation commits, yet the outgoing tiles — and their page markers —
 *   stay in the DOM until the grid's own reset effect runs a commit later.
 *   Without this, a trailing measurement (or the very pointerdown that
 *   started the click) lands in that window and `replaceState`s the
 *   outgoing page onto the incoming URL; since that write skips
 *   revalidation, a later reload would open the new result set at the old
 *   depth.
 * - `replaceState` leaves the router location untouched (no revalidation),
 *   is only called when the settled page differs from the URL, and is
 *   wrapped so a rate-limited write can never throw out of the callback.
 */
export const useViewportPageSync = ({
    enabled,
    trackCategory,
}: UseViewportPageSyncArgs): void => {
    const tracker = useTracker();
    const location = useLocation();
    const trackCategoryRef = useRef(trackCategory);
    trackCategoryRef.current = trackCategory;

    // The route identity this hook is rendering under (page param excluded —
    // this hook is its writer). Compared against window.location at write
    // time so a measurement can never write onto a different route's URL.
    const routeKey = useMemo(() => {
        const params = new URLSearchParams(location.search);
        params.delete('page');
        return `${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search]);
    const routeKeyRef = useRef(routeKey);
    routeKeyRef.current = routeKey;

    // Armed state outlives the listener effect so toggling `enabled` (a result
    // set growing past one page) does not silently re-disarm mid-scroll; only
    // a new route identity does, below.
    const armedRef = useRef(false);
    useEffect(() => {
        armedRef.current = false;
    }, [routeKey]);

    useEffect(() => {
        if (!enabled) return;
        let frame = 0;
        let trailing: ReturnType<typeof setTimeout> | undefined;
        let lastMeasure = 0;

        const liveRouteKey = () => {
            const params = new URLSearchParams(window.location.search);
            params.delete('page');
            return `${window.location.pathname}?${params.toString()}`;
        };

        const measure = () => {
            frame = 0;
            lastMeasure = Date.now();
            if (!armedRef.current) return;
            // Mid-navigation (POP swaps the URL before the new grid renders):
            // these markers belong to the outgoing route — never write them
            // onto the incoming URL.
            if (liveRouteKey() !== routeKeyRef.current) return;
            const markers = document.querySelectorAll(
                `[${VIEWPORT_PAGE_MARKER_ATTR}]`,
            );
            if (markers.length === 0) return;

            const midline = window.innerHeight / 2;
            let page = 0;
            let minPage = Number.POSITIVE_INFINITY;
            for (const marker of markers) {
                const value = Number(
                    marker.getAttribute(VIEWPORT_PAGE_MARKER_ATTR),
                );
                if (!Number.isFinite(value)) continue;
                minPage = Math.min(minPage, value);
                if (marker.getBoundingClientRect().top <= midline) {
                    page = Math.max(page, value);
                }
            }
            // Above the first marker (page 1's start sits below the fold on
            // pages with intro content): the reader is on the lowest page.
            if (page === 0) {
                if (!Number.isFinite(minPage)) return;
                page = minPage;
            }

            const url = new URL(window.location.href);
            const urlPage = Number(url.searchParams.get('page') ?? '1') || 1;
            if (urlPage === page) return;

            if (page > 1) {
                url.searchParams.set('page', String(page));
            } else {
                url.searchParams.delete('page');
            }
            try {
                window.history.replaceState(window.history.state, '', url);
            } catch {
                // Safari rate-limits replaceState; the next settled
                // measurement writes the (by then current) page instead.
                return;
            }

            if (trackCategoryRef.current) {
                tracker.log('DirectoryPageChange', {
                    url,
                    targetTracker: 'archiproTracker',
                    data: {
                        page,
                        category: trackCategoryRef.current,
                        // Both keys above are dropped by the warehouse's
                        // column flattening, so the series arrived with no
                        // page number on it.
                        Source: 'directoryPageChange',
                        ExtraData: JSON.stringify({
                            page,
                            category: trackCategoryRef.current,
                        }),
                    },
                });
            }
        };

        const scheduleFrame = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        // Throttle with a trailing edge: at most one measurement per
        // interval while scroll events stream, plus one at settle so the
        // final position always gets measured.
        const requestMeasure = () => {
            const elapsed = Date.now() - lastMeasure;
            if (elapsed >= MEASURE_INTERVAL_MS) {
                scheduleFrame();
                return;
            }
            if (trailing) clearTimeout(trailing);
            trailing = setTimeout(scheduleFrame, MEASURE_INTERVAL_MS - elapsed);
        };

        const arm = () => {
            armedRef.current = true;
            requestMeasure();
        };
        const armOnScrollKey = (event: KeyboardEvent) => {
            if (SCROLL_INTENT_KEYS.has(event.key)) arm();
        };

        window.addEventListener('scroll', requestMeasure, { passive: true });
        window.addEventListener('resize', requestMeasure);
        window.addEventListener('wheel', arm, { passive: true });
        window.addEventListener('touchmove', arm, { passive: true });
        window.addEventListener('pointerdown', arm, { passive: true });
        window.addEventListener('keydown', armOnScrollKey, { passive: true });
        return () => {
            if (frame) cancelAnimationFrame(frame);
            if (trailing) clearTimeout(trailing);
            window.removeEventListener('scroll', requestMeasure);
            window.removeEventListener('resize', requestMeasure);
            window.removeEventListener('wheel', arm);
            window.removeEventListener('touchmove', arm);
            window.removeEventListener('pointerdown', arm);
            window.removeEventListener('keydown', armOnScrollKey);
        };
    }, [enabled, tracker]);
};
